import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Engineering as EngineeringIcon,
  DateRange as DateRangeIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Devices as DevicesIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { 
  getAllTechnicians, 
  getTechnicianStats, 
  getTechnicianEarnings 
} from '../services/technicianService';
import { Technician, TechnicianStats, TechnicianEarning, TechnicianSalaryType } from '../utils/types';
import { useCurrency } from '../hooks/useCurrency';

// نوع الفترة الزمنية
type TimeRange = 'today' | 'week' | 'month' | 'year' | 'custom';

const TechnicianReportsPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [selectedTechnician, setSelectedTechnician] = useState<Technician | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [stats, setStats] = useState<TechnicianStats | null>(null);
  const [earnings, setEarnings] = useState<TechnicianEarning[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTechnicians();
  }, []);

  useEffect(() => {
    if (selectedTechnician) {
      loadTechnicianData();
    }
  }, [selectedTechnician, timeRange, customStartDate, customEndDate]);

  const loadTechnicians = async () => {
    try {
      console.log('🔄 تحميل قائمة الفنيين...');
      const techniciansList = await getAllTechnicians(true);
      console.log('👨‍🔧 تم جلب الفنيين:', techniciansList.length, 'فني');
      console.log('📋 قائمة الفنيين:', techniciansList.map(t => ({ id: t.id, name: t.name, isActive: t.isActive })));

      setTechnicians(techniciansList);
      if (techniciansList.length > 0) {
        setSelectedTechnician(techniciansList[0]);
        console.log('✅ تم اختيار الفني الأول:', techniciansList[0].name);
      } else {
        console.warn('⚠️ لا يوجد فنيين نشطين');
        setError('لا يوجد فنيين نشطين في النظام');
      }
    } catch (error) {
      console.error('❌ خطأ في تحميل قائمة الفنيين:', error);
      setError('فشل في تحميل قائمة الفنيين');
    }
  };

  const getDateRange = (): { startDate: Date; endDate: Date } => {
    const now = new Date();
    let endDate = new Date(now);
    let startDate = new Date(now);

    switch (timeRange) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
    }

    return { startDate, endDate };
  };

  const loadTechnicianData = async () => {
    if (!selectedTechnician) {
      console.warn('⚠️ لا يوجد فني محدد');
      return;
    }

    try {
      console.log('🔄 تحميل بيانات الفني:', selectedTechnician.name);
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange();
      console.log('📅 الفترة الزمنية:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        timeRange
      });

      console.log('📊 جلب إحصائيات وأجور الفني...');
      const [technicianStats, technicianEarnings] = await Promise.all([
        getTechnicianStats(selectedTechnician.id, startDate, endDate),
        getTechnicianEarnings(selectedTechnician.id, startDate, endDate)
      ]);

      console.log('✅ تم جلب البيانات:', {
        statsDevices: technicianStats.totalDevices,
        statsEarnings: technicianStats.totalEarnings,
        earningsCount: technicianEarnings.length
      });

      setStats(technicianStats);
      setEarnings(technicianEarnings);
    } catch (error) {
      console.error('❌ خطأ في تحميل بيانات الفني:', error);
      setError('فشل في تحميل بيانات الفني');
    } finally {
      setLoading(false);
    }
  };

  const getSalaryTypeLabel = (type: TechnicianSalaryType): string => {
    switch (type) {
      case TechnicianSalaryType.FIXED:
        return 'راتب ثابت';
      case TechnicianSalaryType.PER_DEVICE:
        return 'بالقطعة';
      case TechnicianSalaryType.PERCENTAGE:
        return 'بالنسبة المئوية';
      case TechnicianSalaryType.VARIABLE:
        return 'قطعة متغيرة';
      default:
        return 'غير محدد';
    }
  };

  const getTimeRangeLabel = (): string => {
    switch (timeRange) {
      case 'today':
        return 'اليوم';
      case 'week':
        return 'آخر أسبوع';
      case 'month':
        return 'آخر شهر';
      case 'year':
        return 'آخر سنة';
      case 'custom':
        return 'فترة مخصصة';
      default:
        return '';
    }
  };

  if (technicians.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          لا يوجد فنيين مسجلين في النظام. يرجى إضافة فنيين أولاً.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <AssessmentIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            تقارير الفنيين
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>اختر الفني</InputLabel>
                <Select
                  value={selectedTechnician?.id || ''}
                  onChange={(e) => {
                    const technician = technicians.find(t => t.id === e.target.value);
                    setSelectedTechnician(technician || null);
                  }}
                  label="اختر الفني"
                >
                  {technicians.map((technician) => (
                    <MenuItem key={technician.id} value={technician.id}>
                      <Box display="flex" alignItems="center">
                        <EngineeringIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {technician.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {technician.specialization}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>الفترة الزمنية</InputLabel>
                <Select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                  label="الفترة الزمنية"
                >
                  <MenuItem value="today">اليوم</MenuItem>
                  <MenuItem value="week">آخر أسبوع</MenuItem>
                  <MenuItem value="month">آخر شهر</MenuItem>
                  <MenuItem value="year">آخر سنة</MenuItem>
                  <MenuItem value="custom">فترة مخصصة</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {timeRange === 'custom' && (
              <>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="من تاريخ"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="إلى تاريخ"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={1}>
              <Button
                variant="contained"
                onClick={loadTechnicianData}
                disabled={loading || !selectedTechnician}
                startIcon={loading ? <CircularProgress size={20} /> : <DateRangeIcon />}
              >
                تحديث
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {selectedTechnician && (
        <>
          {/* Technician Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <EngineeringIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  معلومات الفني
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">الاسم</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedTechnician.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">التخصص</Typography>
                  <Typography variant="body1">
                    {selectedTechnician.specialization}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">نوع الراتب</Typography>
                  <Chip
                    label={getSalaryTypeLabel(selectedTechnician.salaryType)}
                    size="small"
                    color="primary"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">المبلغ</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedTechnician.salaryAmount}
                    {selectedTechnician.salaryType === TechnicianSalaryType.PERCENTAGE ? '%' : ' جنيه'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Statistics Cards */}
          {stats && (
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <DevicesIcon color="primary" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h6">{stats.totalDevices}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          إجمالي الأجهزة
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <MoneyIcon color="success" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h6">
                          {formatCurrency(stats.totalEarnings)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          إجمالي الأجور ({getTimeRangeLabel()})
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <TrendingUpIcon color="warning" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h6">
                          {formatCurrency(stats.averageEarningPerDevice)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          متوسط الأجر لكل جهاز
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <AssessmentIcon color="info" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h6">
                          {Object.keys(stats.devicesByType).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          أنواع الأجهزة
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Device Types Breakdown */}
          {stats && Object.keys(stats.devicesByType).length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  توزيع الأجهزة حسب النوع
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(stats.devicesByType).map(([deviceType, count]) => (
                    <Grid item xs={12} sm={6} md={4} key={deviceType}>
                      <Box
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="h6" color="primary">
                          {count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {deviceType}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Earnings Details */}
          {earnings.length > 0 && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    تفاصيل الأجور ({getTimeRangeLabel()})
                  </Typography>
                  <Button
                    startIcon={<DownloadIcon />}
                    variant="outlined"
                    size="small"
                  >
                    تصدير
                  </Button>
                </Box>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>التاريخ</TableCell>
                        <TableCell>رقم الصيانة</TableCell>
                        <TableCell>نوع الجهاز</TableCell>
                        <TableCell>حجم الجهاز</TableCell>
                        <TableCell>نوع الراتب</TableCell>
                        <TableCell>المبلغ الأساسي</TableCell>
                        <TableCell>الأجر المكتسب</TableCell>
                        <TableCell>تفاصيل الحساب</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {earnings.map((earning) => (
                        <TableRow key={earning.id}>
                          <TableCell>
                            {new Date(earning.date).toLocaleDateString('ar-EG')}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary">
                              #{earning.maintenanceId.slice(-6)}
                            </Typography>
                          </TableCell>
                          <TableCell>{earning.deviceType}</TableCell>
                          <TableCell>{earning.deviceSize || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={getSalaryTypeLabel(earning.salaryType)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {formatCurrency(earning.baseAmount)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(earning.earningAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {earning.calculationDetails}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {loading && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )}

          {!loading && earnings.length === 0 && stats && (
            <Alert severity="info">
              لا توجد أجور مسجلة للفني في الفترة المحددة.
            </Alert>
          )}
        </>
      )}
    </Container>
  );
};

export default TechnicianReportsPage;
